import React, { useEffect } from 'react';
import CreateProvisional from 'components/mutations/CreateProvisionalBooking';
import { withRouter } from 'react-router';
import { useQuery, useApolloClient, useLazyQuery, gql } from '@apollo/client';
import { historyShape, userShape } from 'constants/propTypes';
import { GET_JOURNEYS, CHEAPEST_FARES_SEARCH } from 'store/queries';
import config from 'utils/config';
import { withStations } from 'components/queries/GetStationsQuery';
import JourneySearch from './JourneySearch';
import { getSearchParams } from './JourneySearch.utils';
import groupProductFamilies from 'constants/groupProductFamilies';

import useAnimalsPerTrainsQuery from 'components/queries/useAnimalsPerTrainsQuery';
import useAssistancePerTrainsQuery from 'components/queries/useAssistancePerTrainsQuery';
import useServiceDetailsQuery from 'components/queries/useServiceDetailsQuery';
import { buildCheapestFaresSearchRequest, validProductFamiliesForCheapestFaresSearch } from './CheapestFaresSearch.utils';
import { useIsDesktop } from 'hooks/useBreakpoint';

const Index = (props) => {
  const {
    history: {
      location: { search }
    },
    user: { discountCards } = {}
  } = props;

  const client = useApolloClient();
  const enableShowSummatedFares = config('ENABLE_SHOW_SUMMATED_FARES', { canOverride: true }) === 'true';
  const searchParams = getSearchParams(search, discountCards);
  const membershipNumber = new URLSearchParams(search).get('membershipNumber');
  const cheapestFaresSearchRequest = buildCheapestFaresSearchRequest({
    ...searchParams
  });
  const areProductFamiliesValidForCheapestFaresSearch = validProductFamiliesForCheapestFaresSearch(searchParams);

  const [cheapestFaresRequest, { loading: cheapestFareLoading, data: cheapestFares }] = useLazyQuery(CHEAPEST_FARES_SEARCH, {
    variables: cheapestFaresSearchRequest
  });
  const [animals, { loading: animalsLoading, data: animalsData }] = useAnimalsPerTrainsQuery();
  const [serviceDetails, { loading: serviceDetailsLoading, data: serviceDetailsData }] = useServiceDetailsQuery();
  const [assistance, { loading: assistanceLoading, data: assistanceData }] = useAssistancePerTrainsQuery();

  const isDesktop = useIsDesktop();
  useEffect(() => {
    if (areProductFamiliesValidForCheapestFaresSearch) {
      cheapestFaresRequest();
    }
  }, [search, areProductFamiliesValidForCheapestFaresSearch, cheapestFaresRequest]);

  const isGroupSearch = groupProductFamilies.some((pf) => searchParams.productFamilies?.includes(pf));
  const getJourneysVariables = isGroupSearch
    ? { ...searchParams, maxTransfers: 0, offerSummatedFares: enableShowSummatedFares, useNewLoyaltySystem: true }
    : { ...searchParams, infants: 0, offerSummatedFares: enableShowSummatedFares, useNewLoyaltySystem: true };

  const handleJourneySearchResponse = async ({ journeySearch }) => {
    const { outboundDate, inboundDate, guideDogs, origin, destination } = searchParams;

    await animals({
      variables: {
        search: journeySearch,
        outboundDate,
        inboundDate,
        hasDogs: guideDogs > 0
      }
    });

    await serviceDetails({
      variables: {
        search: journeySearch,
        outboundDate,
        inboundDate,
        origin,
        destination,
        isGroupSearch
      }
    });

    await assistance({
      variables: {
        search: journeySearch,
        outboundDate,
        inboundDate
      }
    });
  };

  const { loading, error, data } = useQuery(GET_JOURNEYS, {
    variables: getJourneysVariables,
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: true,
    onError: () => {
      client.writeQuery({
        query: gql`
          query {
            searchParams @client {
              __typename
              outboundDate
              inboundDate
              origin
              destination
              returningOrigin
              returningDestination
              adults
              adults12To15
              adults16Plus
              children
              children4Only
              children5To11
              infants
              youths
              seniors
              currency
              productFamilies
            }
          }
        `,
        data: {
          searchParams: {
            ...searchParams,
            __typename: 'SearchParams'
          }
        }
      });
    },
    onCompleted: handleJourneySearchResponse
  });

  return (
    <CreateProvisional>
      {({ createProvisionalBooking, loading: loadingMutation }) => (
        <JourneySearch
          {...props}
          loading={loading || loadingMutation || cheapestFareLoading}
          error={error}
          data={data}
          searchParams={searchParams}
          membershipNumber={membershipNumber}
          createProvisionalBooking={createProvisionalBooking}
          cheapestFares={cheapestFares?.cheapestFaresSearch}
          animalsPerTrain={animalsData}
          animalsPerTrainLoading={animalsLoading}
          serviceDetailsData={serviceDetailsData}
          serviceDetailsLoading={serviceDetailsLoading}
          assistancePerTrain={assistanceData}
          assistancePerTrainLoading={assistanceLoading}
          seniorsAges={searchParams.seniorsAges}
          youthAges={searchParams.youthAges}
          childAges={searchParams.childAges}
          client={client}
          isDesktop={isDesktop}
        />
      )}
    </CreateProvisional>
  );
};

Index.propTypes = {
  history: historyShape,
  user: userShape
};

export default withStations(withRouter(Index));
